import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_ID, enableProdMode, isDevMode, signal } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, withEnabledBlockingInitialNavigation } from '@angular/router';
import { CoreEffects } from '@next/core-lib';
import { provideAgrAdvisorRelationshipsSelector } from '@next/core-lib/advisor-relationships-selector';
import { provideAgrCore } from '@next/core-lib/app';
import { I18nBrowserEffects, provideAgrBrowser } from '@next/core-lib/browser';
import { provideAgrBusinessPartnerAggregationApi } from '@next/core-lib/business-partner-aggregation-api';
import { provideAgrElasticApi } from '@next/core-lib/elastic';
import { ENVIRONMENT_MAPPER } from '@next/core-lib/environment-browser';
import { I18nEffects } from '@next/core-lib/i18n';
import { provideAgrNavigation, NavigationEffects } from '@next/core-lib/navigation';
import { provideAgrRouterStore } from '@next/core-lib/router-store';
import { provideAgrSettings } from '@next/core-lib/settings';
import { provideAgrSite, SiteEffects } from '@next/core-lib/site';
import { provideAgrTable } from '@next/core-lib/table';
import { provideEffects } from '@ngrx/effects';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';

import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';
import { AppEffects } from './app/store/effects';
import { appReducer } from './app/store/reducer';
import { setEnvironment } from './environment';
import { environment } from './environments/environment';

if (environment.PRODUCTION) {
  enableProdMode();
}

const loadedConfig = signal(environment);

export const setupRuntimeConfig = (async () => {
  try {
    const response = await globalThis.fetch('/assets/config.json');
    if (response.ok) {
      const config = await response.json();
      loadedConfig.set({ ...environment, ...setEnvironment(config) });
    } else {
      console.warn('No runtime configuration found, using default environment settings.');
    }
  } catch (e) {
    console.warn('Failed to load runtime environment variables', e);
  }
})();

setupRuntimeConfig.then(() => {
  bootstrapApplication(AppComponent, {
    providers: [
      provideRouter(appRoutes, withEnabledBlockingInitialNavigation()),
      provideHttpClient(withInterceptorsFromDi()),
      provideStore(
        {},
        {
          runtimeChecks: {
            strictStateImmutability: true,
            strictActionImmutability: true,
            strictStateSerializability: true,
            strictActionSerializability: true,
          },
        },
      ),
      provideState('app', appReducer),
      provideAgrRouterStore(),
      provideAgrCore(),
      provideAgrBrowser(),
      provideAgrSite(),
      provideAgrNavigation(),
      provideAgrElasticApi(),
      provideAgrAdvisorRelationshipsSelector(),
      provideAgrBusinessPartnerAggregationApi(),
      provideAgrSettings(),
      provideAgrTable(),
      provideEffects([
        AppEffects,
        CoreEffects,
        I18nEffects,
        I18nBrowserEffects,
        SiteEffects,
        NavigationEffects,
      ]),
      provideStoreDevtools({
        maxAge: 25,
        logOnly: !isDevMode(),
        autoPause: true,
        trace: false,
        traceLimit: 75,
        connectInZone: false,
      }),
      {
        provide: ENVIRONMENT_MAPPER,
        useFactory: () => loadedConfig(),
      },
      {
        provide: APP_ID,
        useFactory: () => loadedConfig().APPLICATION_NAME,
      },
    ],
  }).catch((err) => console.error(err));
});
